// src/context/TeacherContext.tsx

import React, { createContext, useState, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import apiAxiosInstance from '../utils/axiosInstance';

// Define the Teacher type
export interface Teacher {
  _id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  gradeLevel?: string;
  teachingStandards?: any[];
  state?: string;
  googleId?: string;
  profilePicture?: string;
  // Identity/ownership (Phase 1 homeschool pivot): defaults preserve every
  // existing account as a teacher. See IMPLEMENTATION_PLAN.md §8.3/§9.4.
  role?: 'teacher' | 'parent';
  familyId?: string | null;
  subscription?: {
    status: 'active' | 'cancelled' | 'none' | 'trial' | 'canceling';
    plan: 'monthly' | 'yearly' | 'none' | 'premium';
    subscriptionId?: string;
    nextBillingDate?: string;
    trialEnd?: number;
    cancelAtPeriodEnd?: boolean;
  };
  // Subject Configuration
  primarySubject?: 'ELA' | 'Math' | 'Science' | 'Social Studies' | 'Reading';
  subjectPreferences?: {
    focusSubject?: string;
    analysisMode?: 'reading-comprehension' | 'literary-analysis' | 'informational-text' | 'general';
    standardsSystem?: 'CCSS' | 'TEKS' | 'State-Specific';
  };
  // Multi-Subject Support (Future-Ready)
  teachingSubjects?: Array<{
    subject: 'ELA' | 'Math' | 'Science' | 'Social Studies' | 'Reading' | 'Art' | 'Music' | 'PE';
    gradeLevel?: string;
    isActive?: boolean;
    preferences?: {
      analysisMode?: string;
      standardsSystem?: string;
      focusAreas?: string[];
    };
  }>;
  // Current Active Subject Context
  currentSubjectContext?: {
    activeSubject?: string;
    lastSwitched?: string;
    analysisPreferences?: Record<string, {
      analysisMode?: string;
      standardsSystem?: string;
      lastUsed?: string;
    }>;
  };
  // Google OAuth tokens for Forms integration
  googleTokens?: {
    access_token?: string;
    refresh_token?: string;
    scope?: string;
    token_type?: string;
    expiry_date?: number;
  };
}

// Define the context type
export interface TeacherContextType {
  teacher: Teacher | null;
  setTeacher: React.Dispatch<React.SetStateAction<Teacher | null>>;
  loading: boolean;
  error: string | null;
  refreshTeacher: () => Promise<void>;
}

// Cache management utilities
const CACHE_KEY = 'teacher_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CachedTeacher {
  data: Teacher;
  timestamp: number;
  token: string;
}

const getCachedTeacher = (): CachedTeacher | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    const parsed: CachedTeacher = JSON.parse(cached);
    const currentToken = localStorage.getItem('token');
    
    // Check if cache is still valid
    if (!currentToken || parsed.token !== currentToken) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    
    // Check if cache has expired
    if (Date.now() - parsed.timestamp > CACHE_DURATION) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    
    return parsed;
  } catch (error) {
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
};

const setCachedTeacher = (teacher: Teacher) => {
  try {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) return;
    
    const cached: CachedTeacher = {
      data: teacher,
      timestamp: Date.now(),
      token: currentToken
    };
    
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch (error) {
    console.error('Failed to cache teacher data:', error);
  }
};

// Create the context with proper typing
export const TeacherContext = createContext<TeacherContextType>({
  teacher: null,
  setTeacher: () => null,
  loading: true,
  error: null,
  refreshTeacher: async () => {}
});

// Create the provider component
export const TeacherProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [lastToken, setLastToken] = useState<string | null>(null);

  // Function to refresh teacher data from API
  const refreshTeacher = async (): Promise<void> => {
    try {
      console.log('Refreshing teacher data from API...');
              const response = await apiAxiosInstance.get('/auth/teacher-profile');
      
      if (response.data.teacher) {
        setTeacher(response.data.teacher);
        setCachedTeacher(response.data.teacher);
        setError(null);
        console.log('Teacher data refreshed successfully');
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error: any) {
      console.error('Failed to refresh teacher data:', error);
      // Don't clear existing data on refresh failure
      setError('Failed to refresh data');
    }
  };

  useEffect(() => {
    // Skip authentication if we're on the OAuth callback route, public assignment route, or public test route
    const isPublicAssignmentRoute = location.pathname.startsWith('/a/');
    const isPublicTestRoute = location.pathname.startsWith('/test/');
    if (location.pathname === '/auth/callback' || isPublicAssignmentRoute || isPublicTestRoute) {
      console.log('Skipping auth initialization - on public route:', location.pathname);
      setLoading(false);
      setInitialized(true);
      return;
    }

    const initializeAuth = async () => {
      console.log('\n=== TeacherContext: Starting Auth ===');
      console.log('Current state:', { initialized, retryCount, loading, error });
      console.log('Teacher state:', teacher ? 'Exists' : 'Null');
      
      try {
        // Use lastToken as fallback
        const token = localStorage.getItem('token') || lastToken;
        console.log('Token exists:', !!token, 'Using cached token:', token === lastToken);
        
        // If no token, clear state and return early
        if (!token) {
          console.log('No token found - clearing state');
          setTeacher(null);
          setError(null);
          setLoading(false);
          setInitialized(true);
          setLastToken(null);
          return;
        }

        // Store token for retry attempts
        setLastToken(token);

        // Check for cached teacher data first
        const cachedTeacher = getCachedTeacher();
        if (cachedTeacher) {
          console.log('Using cached teacher data - loading immediately');
          setTeacher(cachedTeacher.data);
          setError(null);
          setLoading(false);
          setInitialized(true);
          setRetryCount(0);
          
          // Refresh data in background (non-blocking)
          setTimeout(() => {
            console.log('Starting background refresh...');
            refreshTeacher().catch(console.error);
          }, 100);
          
          return;
        }

        // No cache available - show loading and make API call
        console.log('No cached data - showing loading and making API call');
        setLoading(true);
        setError(null);

        // Verify token minimally, then fetch full profile
        console.log('Making verify request...');
        const verifyRes = await apiAxiosInstance.get('/auth/verify');
        console.log('Verify response:', verifyRes.status, !!verifyRes.data?.teacher?._id);

        if (!verifyRes.data?.teacher?._id) {
          console.log('Verify succeeded but no teacher id');
          throw new Error('Invalid verify response');
        }

        console.log('Fetching teacher profile...');
        const profileRes = await apiAxiosInstance.get('/auth/teacher-profile');
        if (profileRes.data?.teacher) {
          console.log('Teacher profile loaded - updating state and cache');
          setTeacher(profileRes.data.teacher);
          setCachedTeacher(profileRes.data.teacher);
          setError(null);
          setRetryCount(0);
          setInitialized(true);
        } else {
          console.log('Invalid profile response - no teacher data');
          throw new Error('Invalid profile response');
        }
      } catch (error: any) {
        console.error('\n=== Auth Error Details ===');
        console.log('Error type:', error.name);
        console.log('Error message:', error.message);
        console.log('Response status:', error.response?.status);
        console.log('Error code:', error.code);
        console.log('Current retry count:', retryCount);
        console.log('Has cached token:', !!lastToken);
        
        // Keep existing state for temporary errors
        const isTemporaryError = 
          error.code === 'ECONNABORTED' || 
          error.message?.includes('timeout') ||
          error.response?.status === 503 ||
          (error.name === 'Error' && (
            error.message.includes('timed out') ||
            error.message.includes('Server busy')
          ));
        
        console.log('Is temporary error:', isTemporaryError);
        
        // For temporary errors, try to use cached data if available
        if (isTemporaryError) {
          const cachedTeacher = getCachedTeacher();
          if (cachedTeacher) {
            console.log('Using cached data due to temporary error - OFFLINE MODE');
            setTeacher(cachedTeacher.data);
            setError('Backend slow - using offline mode');
            setInitialized(true);
            return;
          }
        }
        
        if (isTemporaryError && retryCount < 1) { // Reduced retries
          console.log('Attempting retry...');
          setRetryCount(prev => prev + 1);
          setError('Backend slow - retrying...');
          setTimeout(() => {
            console.log('Triggering retry after timeout');
            setInitialized(false);
          }, 500); // Reduced retry delay to 500ms
          return;
        }
        
        // If all else fails, try to work with cached data
        const cachedTeacher = getCachedTeacher();
        if (cachedTeacher) {
          console.log('Max retries reached - using cached data for offline mode');
          setTeacher(cachedTeacher.data);
          setError('Offline mode - using cached data');
          setInitialized(true);
          return;
        }
        
        // Only clear auth state for actual auth failures
        if (error.response?.status === 401) {
          console.log('Auth failure - clearing token and state');
          localStorage.removeItem('token');
          localStorage.removeItem(CACHE_KEY);
          setLastToken(null);
          setTeacher(null);
          setError('Session expired. Please login again.');
        } else if (isTemporaryError) {
          console.log('Temporary error - keeping state and showing message');
          setError('Connection issue. Please wait...');
        } else {
          console.log('Other error - keeping current state');
          setError(error.response?.data?.message || 'Authentication failed');
        }
        setInitialized(true);
      } finally {
        console.log('\n=== Auth Flow Complete ===');
        console.log('Final state:', { 
          loading: false, 
          initialized: true, 
          retryCount,
          hasToken: !!localStorage.getItem('token'),
          hasCachedToken: !!lastToken
        });
        setLoading(false);
      }
    };

    if (!initialized) {
      console.log('Starting initialization...');
      initializeAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, retryCount, location.pathname]);

  return (
    <TeacherContext.Provider value={{ 
      teacher, 
      setTeacher, 
      loading, 
      error,
      refreshTeacher 
    }}>
      {children}
    </TeacherContext.Provider>
  );
};

export const useTeacher = () => {
  const context = useContext(TeacherContext);
  if (!context) {
    throw new Error('useTeacher must be used within a TeacherProvider');
  }
  return context;
};
